import { JiraIssueField } from '@/api/jira.hook'

import { formatDate } from '@/lib/date/date'

import { CheckboxGroup } from '@/components/lemmala-design-system/checkbox-group/checkbox-group'
import { Input } from '@/components/lemmala-design-system/input/input'
import { Select } from '@/components/lemmala-design-system/select/select'

type JiraFieldInputProps = {
  field: JiraIssueField
  value?: unknown // NOTE these properties are injected by the parent <Form> component
  onChange?: (value: unknown) => void // NOTE these properties are injected by the parent <Form> component
}

export function JiraFieldInput({ field, value, onChange }: JiraFieldInputProps) {
  const fieldType = field.schema.type

  const defaultValue = field.hasDefaultValue
    ? field.allowedValues?.find((allowedValue) => allowedValue.id === value)?.id
    : undefined

  switch (fieldType) {
    case 'option':
    case 'priority':
      // NOTE: the value is expected to be { id: string } in the API request
      // So we need to transform the props value from string to { id: string }
      return (
        <Select<string>
          options={(field.allowedValues ?? []).map((allowedValue) => ({
            // allowedValue.value is the label in fieldType 'option', allowedValue.name is the label in fieldType 'priority'
            label: allowedValue.value ?? allowedValue.name,
            value: allowedValue.id ?? '',
          }))}
          placeholder='Select'
          value={(value as { id: string })?.id ?? defaultValue ?? ''}
          onChange={(next) => onChange?.({ id: next })}
          className='w-full'
        />
      )
    case 'date': {
      // Date field - Jira expects "YYYY-MM-DD" string format
      // Handle both Date objects and strings
      let dateString = ''
      if (value instanceof Date) {
        dateString = formatDate(value, 'yyyyMMdd')
      } else if (typeof value === 'string') {
        dateString = value
      }

      return (
        <Input
          type='date'
          value={dateString ?? defaultValue ?? ''} // If no value is provided, use an empty string to force controlled component behavior
          onChange={(e) => {
            // Jira API expects "YYYY-MM-DD" format string
            onChange?.(e.target.value || undefined)
          }}
          className='w-full'
        />
      )
    }
    case 'number':
      // NOTE: the value is expected to be number in the API request
      return (
        <Input
          value={(value as number) ?? defaultValue ?? 0} // If no value is provided, use 0 to force controlled component behavior
          onChange={(e) => onChange?.(Number(e.target.value))}
          type='number'
          className='w-full'
        />
      )
    case 'array':
      // NOTE: the value is expected to be array of strings in the API request
      // If the field has no allowed values, set it as a comma separated list in a text input
      if (!field.allowedValues) {
        return (
          <Input
            value={((value as string[]) ?? []).join(',') ?? defaultValue ?? ''} // If no value is provided, use an empty array to force controlled component behavior
            onChange={(e) =>
              onChange?.(e.target.value.split(',').map((val) => val.replace(' ', '_')))
            }
            className='w-full'
          />
        )
      }
      return (
        <CheckboxGroup
          options={(field.allowedValues ?? []).map((allowedValue) => ({
            label: allowedValue.value,
            value: allowedValue.id,
          }))}
          value={((value as { id: string }[]) ?? []).map((item) => item.id) ?? defaultValue ?? []} // If no value is provided, use an empty array to force controlled component behavior
          onChange={(value) => onChange?.(value.map((id) => ({ id })))}
          className='w-full'
        />
      )
    case 'string':
      // NOTE: the value is expected to be string in the API request
      return (
        <Input
          value={(value as string) ?? defaultValue ?? ''} // If no value is provided, use an empty string to force controlled component behavior
          onChange={(e) => onChange?.(e.target.value)}
          className='w-full'
        />
      )
    default:
      console.warn(`Unsupported field type: ${fieldType}, assuming string`)
      // Assume fieldType === 'string' by default
      // NOTE: the value is expected to be string in the API request
      return (
        <Input
          value={(value as string) ?? defaultValue ?? ''} // If no value is provided, use an empty string to force controlled component behavior
          onChange={(e) => onChange?.(e.target.value)}
          className='w-full'
        />
      )
  }
}
